import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin, ChevronDown, ChevronUp, Facebook, Instagram, Linkedin } from "lucide-react";
import api from "@/lib/api";
import Turnstile from "@/components/Turnstile";
import { COMPANY } from "@/lib/content";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [tsToken, setTsToken] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email, source: "footer", turnstile_token: tsToken });
      toast.success("Subscribed. Welcome to Fortune Insights.");
      setEmail("");
    } catch { toast.error("Subscription failed. Try again."); }
    finally { setSubscribing(false); }
  };

  return (
    <footer className="bg-[#1C3E5F] text-white/80" data-testid="site-footer">
      {/* Newsletter strip */}
      <div className="border-b border-[#B8965A]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-[#B8965A] mb-2">Fortune Insights</div>
            <div className="font-serif text-white text-2xl">Market updates · freight alerts · China intelligence</div>
          </div>
          <form onSubmit={subscribe} className="lg:col-span-7 flex gap-3" data-testid="newsletter-form">
            <input data-testid="newsletter-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 bg-transparent border border-white/20 px-5 py-3 text-white placeholder-white/40 focus:border-[#B8965A] outline-none text-sm"/>
            <button data-testid="newsletter-submit" disabled={subscribing} className="btn-primary whitespace-nowrap">{subscribing ? "…" : "Subscribe"}</button>
          </form>
          <div className="lg:col-span-7"><Turnstile action="newsletter" onVerify={setTsToken} /></div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <img src="/fortune-logo-white.png" alt="Fortune Sourcings" className="h-14 w-auto mb-3"/>
            <div className="eyebrow text-[#B8965A] text-[10px] mb-5">Est. {COMPANY.established} · Third Generation</div>
            <p className="text-sm leading-relaxed text-white/70 max-w-md mb-6">
              {COMPANY.positioning}. Operated by {COMPANY.legal}, a third-generation sourcing and export logistics business with permanent on-ground presence across China.
            </p>
            <div className="flex flex-col gap-2 text-[13px]">
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-[#B8965A]" data-testid="footer-email"><Mail size={14}/> {COMPANY.email}</a>
              {COMPANY.people.map(p => (
                <a key={p.name} href={p.whatsapp} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 hover:text-[#B8965A]"
                  data-testid={`footer-whatsapp-${p.name.split(" ")[0].toLowerCase()}`}>
                  <MessageCircle size={14}/> {p.name} ({p.role}) · {p.phone}
                </a>
              ))}
              <span className="text-white/50 text-[11px] pl-[22px]">WhatsApp &amp; WeChat on the same numbers</span>
            </div>
            <div className="flex gap-3 mt-6">
              {[
                { href: COMPANY.socials.facebook, Icon: Facebook, label: "Facebook" },
                { href: COMPANY.socials.instagram, Icon: Instagram, label: "Instagram" },
                { href: COMPANY.socials.linkedin, Icon: Linkedin, label: "LinkedIn" },
              ].filter((s) => Boolean(s.href)).map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                  className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#B8965A] hover:text-[#B8965A] transition-colors">
                  <Icon size={14}/>
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="eyebrow text-[#B8965A] mb-5">Global Offices</div>
            <div className="grid md:grid-cols-3 gap-6">
              {COMPANY.offices.map(o => <OfficeCard key={o.key} office={o}/>)}
            </div>
            <div className="mt-6 text-xs text-white/60">Regional presence: <span className="text-white/85">{COMPANY.regionalPresence.join(" · ")}</span></div>
          </div>

          <FooterCol className="lg:col-span-3" title="Explore" links={[
            { label: "Services", to: "/services" },
            { label: "Process", to: "/process" },
            { label: "Fees", to: "/fees" },
            { label: "Clusters Map", to: "/clusters" },
            { label: "India–Nepal Corridor", to: "/corridors" },
            { label: "Import Compliance", to: "/import" },
            { label: "Insights", to: "/insights" },
            { label: "Tools", to: "/tools" },
            { label: "Careers", to: "/careers" },
            { label: "Contact", to: "/contact" },
          ]}/>
        </div>

        {/* Login strip */}
        <div className="mt-12 pt-8 border-t border-white/10" data-testid="footer-login-strip">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="eyebrow text-[#B8965A] mb-1">Sign in to the operating system</div>
              <div className="text-sm text-white/65">Three doors, same Fortune backbone.</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/login?as=team" data-testid="footer-login-team" className="btn-outline border-white/30 text-white hover:bg-[#B8965A] hover:text-[#12283F] hover:border-[#B8965A] !py-2 !px-4 !text-[11px]">Team Login</Link>
              <Link to="/login?as=client" data-testid="footer-login-client" className="btn-outline border-white/30 text-white hover:bg-[#B8965A] hover:text-[#12283F] hover:border-[#B8965A] !py-2 !px-4 !text-[11px]">Client Login</Link>
              <Link to="/login?as=vendor" data-testid="footer-login-vendor" className="btn-outline border-white/30 text-white hover:bg-[#B8965A] hover:text-[#12283F] hover:border-[#B8965A] !py-2 !px-4 !text-[11px]">Vendor Login</Link>
            </div>
          </div>
        </div>

        {/* Our Policies — dedicated strip */}
        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-policies-strip">
          <div className="eyebrow text-[#B8965A] mb-4">Our Policies</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2 text-[12.5px]">
            {[
              { slug: "privacy",           label: "Privacy Policy" },
              { slug: "terms",             label: "Terms of Engagement" },
              { slug: "payment",           label: "Service & Payment" },
              { slug: "shipping",          label: "Shipping & Logistics" },
              { slug: "refund",            label: "Return, Refund & Cancellation" },
              { slug: "cookies",           label: "Cookie Policy" },
              { slug: "data-protection",   label: "Data Protection & GDPR" },
              { slug: "anti-corruption",   label: "Anti-Corruption & Anti-Bribery" },
              { slug: "supplier-code",     label: "Supplier Code of Conduct" },
              { slug: "employee-code",     label: "Employee Code of Conduct" },
              { slug: "terms-of-use",      label: "Website Terms of Use" },
              { slug: "sanctions",         label: "Sanctions & Trade Compliance" },
            ].map((p) => (
              <Link key={p.slug} to={`/legal/${p.slug}`} data-testid={`footer-policy-${p.slug}`} className="text-white/70 hover:text-[#B8965A] transition-colors">{p.label}</Link>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} {COMPANY.legal}. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/legal/privacy" className="hover:text-[#B8965A]" data-testid="footer-privacy">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-[#B8965A]" data-testid="footer-terms">Terms</Link>
            <Link to="/legal/cookies" className="hover:text-[#B8965A]">Cookies</Link>
            <button
              onClick={() => window.dispatchEvent(new Event("fs:open-cookie-prefs"))}
              className="hover:text-[#B8965A] text-left"
              data-testid="footer-cookie-prefs"
            >
              Cookie Preferences
            </button>
            <Link to="/faq" className="hover:text-[#B8965A]">FAQ</Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`}
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#B8965A]"
              data-testid="footer-sitemap"
            >
              Sitemap
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function OfficeCard({ office }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-[12.5px] border border-white/10 hover:border-[#B8965A]/40 p-3 transition-colors" data-testid={`office-${office.key}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`office-toggle-${office.key}`}
        className="w-full text-left flex items-start gap-2 group"
      >
        <MapPin size={12} className="text-[#B8965A] mt-1 shrink-0"/>
        <div className="flex-1">
          <div className="font-serif text-white text-lg leading-tight">{office.name}</div>
          <div className="eyebrow text-[#B8965A] text-[9px] mt-1">{office.short}</div>
          {/* Compact overview when collapsed */}
          {!open && (
            <div className="mt-2 text-white/60 text-[11.5px]">
              {office.lines?.[office.lines.length - 1] || office.short}
              {office.phone && <> · <span className="text-white/85">{office.phone}</span></>}
            </div>
          )}
        </div>
        <ChevronDown size={13} className={`text-[#B8965A]/70 mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {/* Full details drop-down — kept in the DOM (hidden with CSS) so crawlers
          still see the trust signals per A5.1, but visually opt-in for humans. */}
      <div className={open ? "block" : "hidden"}>
        <address className="mt-3 ml-5 pl-3 border-l border-[#B8965A]/30 text-white/70 space-y-0.5 not-italic text-[11.5px]">
          {office.lines.map((l, i) => <div key={i}>{l}</div>)}
          {office.phone && (
            <div className="mt-1.5">
              <a href={`tel:${office.phone.replace(/[^\d+]/g, "")}`} className="hover:text-[#B8965A]" data-testid={`office-phone-${office.key}`}>
                {office.phone}
              </a>
            </div>
          )}
          {office.gst && <div className="mt-1 text-white/55">GSTIN · {office.gst}</div>}
          {office.iec && <div className="text-white/55">IEC · {office.iec}</div>}
          {office.pan && <div className="text-white/55">PAN · {office.pan}</div>}
        </address>
      </div>
      {/* Hidden SEO block — always rendered for crawlers, invisible to sighted users */}
      <div className="sr-only" aria-hidden="true">
        {office.lines.join(", ")}
        {office.phone && ` · ${office.phone}`}
        {office.gst && ` · GSTIN ${office.gst}`}
        {office.iec && ` · IEC ${office.iec}`}
        {office.pan && ` · PAN ${office.pan}`}
      </div>
    </div>
  );
}

function FooterCol({ title, links, className = "" }) {
  return (
    <div className={className}>
      <div className="eyebrow text-[#B8965A] mb-5">{title}</div>
      <ul className="flex flex-col gap-1 text-[13px]">
        {links.map((l) => (
          <li key={l.to}><Link to={l.to} className="inline-block py-2 text-white/70 hover:text-[#B8965A] transition-colors">{l.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
